import { useState, useMemo, useEffect, useRef } from "react"
import { animate, AnimatePresence, motion } from "framer-motion"
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid } from "recharts"
import {
  addDays,
  format,
  subDays,
  subMonths,
  startOfDay,
  startOfWeek,
  startOfMonth,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval,
} from "date-fns"

import { BarChart2 } from "lucide-react"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/motion-tabs"

const PERIODS = [
  { value: "7d",  label: "7d"  },
  { value: "30d", label: "30d" },
  { value: "3m",  label: "3m"  },
]

const PERIOD_INFO = {
  "7d":  { num: 7,  unit: "days"   },
  "30d": { num: 30, unit: "days"   },
  "3m":  { num: 3,  unit: "months" },
}

const chartConfig = {
  runs: {
    label: "Runs",
    color: "#0ea5e9",
  },
}

function getChartData(runs, period) {
  const now = new Date()
  let periodStart, intervals, getBucket, formatLabel

  let formatTooltipLabel
  if (period === "7d") {
    periodStart = startOfDay(subDays(now, 6))
    intervals = eachDayOfInterval({ start: periodStart, end: startOfDay(now) })
    getBucket = (date) => format(startOfDay(date), "yyyy-MM-dd")
    formatLabel = (date) => format(date, "EEE")
    formatTooltipLabel = (date) => format(date, "EEE, MMM d")
  } else if (period === "30d") {
    periodStart = startOfDay(subDays(now, 29))
    intervals = eachDayOfInterval({ start: periodStart, end: startOfDay(now) })
    getBucket = (date) => format(startOfDay(date), "yyyy-MM-dd")
    formatLabel = (date) => format(date, "MMM d")
    formatTooltipLabel = (date) => format(date, "EEE, MMM d")
  } else if (period === "3m") {
    periodStart = startOfWeek(subMonths(now, 3), { weekStartsOn: 1 })
    intervals = eachWeekOfInterval({ start: periodStart, end: now }, { weekStartsOn: 1 })
    getBucket = (date) => format(startOfWeek(date, { weekStartsOn: 1 }), "yyyy-MM-dd")
    formatLabel = (date) => format(date, "MMM d")
    formatTooltipLabel = (date) => `Week of ${format(date, "MMM d")}`
  } else {
    periodStart = startOfMonth(subMonths(now, 11))
    intervals = eachMonthOfInterval({ start: periodStart, end: now })
    getBucket = (date) => format(startOfMonth(date), "yyyy-MM")
    formatLabel = (date) => format(date, "MMM")
    formatTooltipLabel = (date) => format(date, "MMMM yyyy")
  }

  const bucketMap = {}
  for (const interval of intervals) {
    bucketMap[getBucket(interval)] = 0
  }

  for (const run of runs) {
    const runDate = new Date(run.created_at)
    if (runDate < periodStart) continue
    const bucket = getBucket(runDate)
    if (bucket in bucketMap) bucketMap[bucket]++
  }

  return intervals.map((date) => ({
    date: formatLabel(date),
    tooltipDate: formatTooltipLabel(date),
    rawDate: date,
    runs: bucketMap[getBucket(date)] || 0,
  }))
}

function AnimatedNumber({ value }) {
  const ref = useRef(null)
  const prev = useRef(value)

  useEffect(() => {
    const node = ref.current
    const from = prev.current
    prev.current = value

    const controls = animate(from, value, {
      duration: 0.5,
      ease: "easeOut",
      onUpdate(v) {
        if (node) node.textContent = Math.round(v).toLocaleString()
      },
    })

    return () => controls.stop()
  }, [value])

  return <span ref={ref}>{value.toLocaleString()}</span>
}

function RoundedBar({ x, y, width, height, fill, className }) {
  if (!width || height <= 0) return null
  const r = Math.min(3, width / 2, height)
  const d = `M${x},${y + r} Q${x},${y} ${x + r},${y} H${x + width - r} Q${x + width},${y} ${x + width},${y + r} V${y + height} H${x} Z`
  return <path d={d} className={className} fill={fill} />
}

export function RunsChart({ runs = [], onBarClick, activeFrom }) {
  const [period, setPeriod] = useState("7d")
  const [selectedKey, setSelectedKey] = useState(null)
  const [hoveredKey, setHoveredKey] = useState(null)

  // Clear selection when period changes
  useEffect(() => { setSelectedKey(null); setHoveredKey(null) }, [period])

  // Clear selection when the date filter is cleared externally
  useEffect(() => { if (!activeFrom) setSelectedKey(null) }, [activeFrom])

  const chartData = useMemo(() => getChartData(runs, period), [runs, period])
  const total = chartData.reduce((sum, d) => sum + d.runs, 0)

  const xAxisInterval = period === "30d" ? 6 : period === "3m" ? 2 : 0

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-4 justify-between">
        <h2 className="text-md font-medium flex items-center gap-1.5">
          <BarChart2 className="h-4 w-4 text-muted-foreground" />
          Overview
        </h2>
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList className="h-8 items-stretch">
            {PERIODS.map((p) => (
              <TabsTrigger key={p.value} value={p.value} className="text-xs py-0 flex">
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="rounded-lg border border-input bg-card overflow-hidden flex">
        <div className="flex-1 p-4" onMouseDown={(e) => e.preventDefault()}>
          <ChartContainer config={chartConfig} className="h-[160px] w-full [&_.recharts-bar-rectangle_.recharts-rectangle]:[fill-opacity:1] [&_.recharts-rectangle.recharts-tooltip-cursor]:hidden [&_*:focus]:[outline:none] [&_*:focus]:[box-shadow:none]">
            <BarChart key={period} data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: -8 }}>
              <YAxis hide width={0} />
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
                tickMargin={4}
                height={28}
                interval={xAxisInterval}
                padding={{ left: 20, right: 10 }}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    indicator="line"
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.tooltipDate}
                  />
                }
              />
              <Bar
                dataKey="runs"
                fill="#0ea5e9"
                maxBarSize={28}
                background={false}
                activeBar={false}
                shape={RoundedBar}
                onClick={onBarClick ? (data) => {
                  setSelectedKey(data.rawDate.getTime())
                  const to = period === "3m" ? addDays(data.rawDate, 6) : data.rawDate
                  onBarClick({ from: data.rawDate, to })
                } : undefined}
                onMouseEnter={onBarClick ? (data) => setHoveredKey(data?.rawDate?.getTime() ?? null) : undefined}
                onMouseLeave={onBarClick ? () => setHoveredKey(null) : undefined}
                className={onBarClick ? "cursor-pointer" : undefined}
              >
                {onBarClick && chartData.map((entry) => {
                  const key = entry.rawDate.getTime()
                  const isActive = key === selectedKey || key === hoveredKey
                  return <Cell key={key} fill={isActive ? "#0369a1" : "#0ea5e9"} />
                })}
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>

        <div className="flex flex-col items-center justify-center border-l border-input px-8 w-[200px] shrink-0 gap-2">
          <div className="flex flex-col items-center gap-0.5">
            <p className="text-3xl font-bold tabular-nums leading-none">
              <AnimatedNumber value={total} />
            </p>
            <span className="text-sm font-medium uppercase tracking-wider text-foreground">Runs</span>
          </div>
          <p className="text-sm text-muted-foreground text-center whitespace-nowrap">
            In the last{" "}
            <AnimatedNumber value={PERIOD_INFO[period].num} />
            {" "}
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={PERIOD_INFO[period].unit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {PERIOD_INFO[period].unit}
              </motion.span>
            </AnimatePresence>
          </p>
        </div>
      </div>
    </div>
  )
}
